import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMoulds,
  deleteMould,
  setMouldEdit,
  clearMouldEdit,
} from '../../../../redux/actions/masters/mould/mould/mouldActions';
import dayjs from 'dayjs';
import { showConfirm, showInfo } from '../../../../utills/sweetAlert';
import SearchIcon from '@mui/icons-material/Search';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};
const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};
const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};
const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};
const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};
const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853',
  fontSize: 20,
  pointerEvents: 'none',
};
const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const MouldTable = ({ onNew }) => {
  const dispatch = useDispatch();
  const { list = [], loading, error } = useSelector((state) => state.mould);
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});

  useEffect(() => {
    dispatch(fetchMoulds());
  }, [dispatch]);

  const handleEdit = async (mould) => {
    await showInfo('Edit Mould', `Editing mould: ${mould.mould_name}`);
    dispatch(setMouldEdit(mould));
  };

  const handleDelete = async (id, name) => {
    const confirmed = await showConfirm('Trash Mould', `Are you sure you want to trash mould: ${name}?`, 'Trash', 'Cancel');
    if (confirmed) {
      dispatch(deleteMould(id));
    }
  };

  const handleNew = () => {
    dispatch(clearMouldEdit());
    if (typeof onNew === 'function') onNew();
  };

  const filteredMoulds = list.filter(mould =>
    mould.mould_name && mould.mould_name.toLowerCase().includes(search.toLowerCase()) && mould.trash === 0
  );

  const columns = [
    {
      name: 'Mould Code',
      selector: row => row.mould_code,
      sortable: true,
    },
    {
      name: 'Mould Name',
      selector: row => row.mould_name,
      sortable: true,
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ color: row.status === 'Active' ? '#00C853' : '#d32f2f', fontWeight: 600 }}>{row.status}</span>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <>
          <button
            style={actionStyle}
            onClick={() => handleEdit(row)}
            title="Edit"
            onMouseEnter={() => setHoveredAction({ id: row.id, type: 'edit' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <EditIcon fontSize="small" />
            {hoveredAction.id === row.id && hoveredAction.type === 'edit' && (
              <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
            )}
          </button>
          <button
            style={actionStyle}
            onClick={() => handleDelete(row.mould_id, row.mould_name)}
            title="Trash"
            onMouseEnter={() => setHoveredAction({ id: row.mould_id, type: 'delete' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <DeleteOutlineIcon fontSize="small" />
            {hoveredAction.id === row.mould_id && hoveredAction.type === 'delete' && (
              <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Trash</span>
            )}
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '140px',
    },
  ];

  // Helper to check if error is a handled code error
  const isHandledCodeError = error && (
    error.includes('409') ||
    error.toLowerCase().includes('already exists') ||
    error.toLowerCase().includes('cannot be reused')
  );

  return (
    <div style={cardStyle}>
      <style>{`
        .mould-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .mould-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .mould-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <span style={titleStyle}>Mould List</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={newBtnStyle} onClick={handleNew}>+ New</button>
          <div style={searchContainer}>
            <input
              style={searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <SearchIcon style={{ ...searchIconStyle, color: '#00C853' }} />
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredMoulds}
        pagination
        highlightOnHover
        striped
        progressPending={loading}
        noDataComponent={<div style={{ color: '#888', fontSize: 16 }}>No Moulds found.</div>}
        customStyles={{
          headRow: { style: { background: '#E8F5E9', fontWeight: 700 } },
          rows: { style: { minHeight: 48 } },
        }}
        className="mould-table"
      />
      {error && !isHandledCodeError && <div style={{ color: 'red', fontSize: 15, marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default MouldTable; 